<script setup lang="ts">
import { NotepadIcon } from '@blpde/nx-icons';
import {
  computed,
  inject,
  nextTick,
  onBeforeUnmount,
  ref,
  type CSSProperties,
  type Component,
  type StyleValue,
  useTemplateRef,
  watch,
} from 'vue';
import { NxButton } from './../Button';
import { NxEditor } from './../Editor';
import { NxModal } from './../Modal';
import { NxPopover } from './../Popover';
import NxFakeInput from './NxFakeInput.vue';
import { i18nInjectionKey } from '../internal';
import type { InputHeight } from './internal';
// import DOMPurify from 'dompurify';

const props = defineProps<{
  /** Alternate HTML editor surface; the kit retains ownership of dialogs and drafts. */
  editorComponent?: Component;
  /** Host-specific surface options; the UI kit never imports the editor implementation. */
  editorProps?: Record<string, unknown>;
  id?: string;
  wrapperStyle?: StyleValue;
  height?: InputHeight;
  disabled?: boolean;
  editorTitle: string;
  /**
   * Remembers the size of the full editor window under `shell.panes.<key>` (NxModal
   * `storageKey`, module key first). Without it the window opens at its natural size.
   */
  modalStorageKey?: string;
  /**
   * When `true`, the editor (notepad) icon is hidden until the field is hovered
   * or focused, then fades in — its space stays reserved, so nothing shifts.
   * Useful in dense layouts (e.g. table cells) to cut visual noise. Opt-in;
   * defaults to `false`.
   */
  revealIcon?: boolean;
  resizable?: boolean;
  /** Sizes the inline editor to this many text lines. When not `resizable`
   *  it's a hard cap (content beyond scrolls); when `resizable` it's only the
   *  default height and the user can still drag the field taller. Omit for
   *  unbounded growth. */
  maxLines?: number;
  /**
   * Fill a flex-column parent (I-2563): the field — its wrapper, its frame and the inline
   * editor — grows into whatever height the parent gives it, and has no native resize, because
   * another piece drives that height (a modal's `NxModalResizeGrip`). `maxLines` stays the
   * default height. Opt-in; without it nothing changes.
   */
  fill?: boolean;
  /**
   * Dense single-row mode: instead of the inline editor the field shows a
   * truncated one-line plain-text preview (no wrapping, no scrollbars).
   * Clicking the field (or Enter) opens a popover with the editor for quick
   * edits; a button inside it hands off to the full-size modal editor. A
   * double click (or Shift+Enter) and the notepad icon open the modal
   * directly, carrying the quick editor's draft along.
   */
  popoverEdit?: boolean;
  /**
   * The plain text of the value, for the `popoverEdit` preview — for an
   * editor surface whose value is no HTML (a stored document, e.g. Write's
   * `.writez`). Defaults to the text content of the HTML value.
   */
  previewText?: (value: string) => string;
}>();

const emit = defineEmits<{
  'draft-change': [html: string];
  'draft-discard': [];
}>();

// Height for `maxLines` lines: the field's own geometry (style.css) — its
// lines (1lh each, the control line-height; the inline editor's paragraphs sit
// on it, see the unscoped rule below) plus block padding and border, exactly
// like a single-line input of the same `height`. `.fake-input` already has
// `overflow: auto`, so content beyond the height scrolls.
const linesHeight = computed<string | undefined>(() => {
  if (props.maxLines === undefined) return undefined;
  const lines = Math.max(1, props.maxLines);
  return `calc(${lines} * 1lh + 2 * var(--nxt-space-xs) + 2 * var(--nxt-hairline))`;
});

/** One level of the fill chain: a column that grows into its parent (`fill`). */
const FILL: CSSProperties = { display: 'flex', flexDirection: 'column', flex: '1 0 auto' };

// Resizable → the line count is just the *default* height (no max-height, so
// the user can drag past it). Fixed → it's a hard cap (max-height + scroll).
const fakeInputStyle = computed<CSSProperties>(() => {
  const style: CSSProperties = {
    resize: props.resizable && !props.fill ? 'both' : undefined,
    ...(props.fill ? FILL : {}),
    overflow: 'auto',
    scrollbarWidth: 'thin',
    scrollbarColor: 'var(--nxt-fg-subtle) transparent',
  };
  const h = linesHeight.value;
  if (h) {
    // A filling field grows past its lines too: they are its default height, never a cap.
    if (props.resizable || props.fill) style.height = h;
    else style.maxHeight = h;
  }
  return style;
});

const wrapperStyle = computed<StyleValue>(() =>
  props.fill ? [props.wrapperStyle, FILL] : props.wrapperStyle
);

const isOpen = ref<boolean>(false);
const inFocus = ref<boolean>(false);
const modelValue = defineModel<string>({ required: true });

type EditorSurface = InstanceType<typeof NxEditor> & {
  prepareSave?: () => Promise<void> | undefined;
};
const editorRef = useTemplateRef<EditorSurface>('editor-ref');
const smallEditorRef = useTemplateRef<EditorSurface>('small-editor-ref');

// --- popoverEdit mode -------------------------------------------------------
const popoverOpen = ref<boolean>(false);
const popoverEditorRef = useTemplateRef<EditorSurface>('popover-editor-ref');
const previewRef = useTemplateRef('preview-ref');

/** One-line plain-text projection of the value for the truncated preview. */
const previewLine = computed<string>(() => {
  const value = modelValue.value ?? '';
  if (!value) return '';
  const text = props.previewText
    ? props.previewText(value)
    : (new DOMParser().parseFromString(value, 'text/html').body.textContent ?? '');
  return text.replace(/\s+/g, ' ').trim();
});

watch(
  () => {
    return { editor: popoverEditorRef.value, value: modelValue.value };
  },
  ({ editor, value }, previous) => {
    if (!editor) return;
    // Seed when the popover editor first appears. While it is open, inbound
    // modelValue (host PATCH echo after Ctrl/S) must not wipe later typing.
    const editorJustMounted = editor !== previous?.editor;
    if (popoverOpen.value && !editorJustMounted) return;
    editor.setHTML(value);
  }
);

async function commitPopoverEditor(): Promise<boolean> {
  const editor = popoverEditorRef.value;
  if (!editor || props.disabled) return false;
  try {
    const pending = editor.prepareSave?.();
    if (!pending) {
      modelValue.value = editor.getHTML();
      return true;
    }
    const html = editor.getHTML();
    await pending;
    // prepareSave already succeeded — write the drained instance even if the
    // Vue ref was replaced (host remount on blur). Pre-await HTML is only a
    // fallback if that instance's getHTML throws.
    commitEditorHtml(editor, html);
    return true;
  } catch {
    popoverOpen.value = true;
    return false;
  }
}

/** After open + setHTML, put the caret in the editor so the user can type. */
async function focusEditor(getEditor: () => { focus?: () => void } | null): Promise<void> {
  await nextTick();
  await nextTick();
  requestAnimationFrame(() => getEditor()?.focus?.());
}

/**
 * The quick editor takes the caret the moment its panel shows (NxPopover
 * `opened`, in the same task as `showPopover`). Its own focus on mount comes
 * while the panel is still hidden and fails; a focus a frame later let the keys
 * typed in between land on the preview, and the draft was lost (BLP/nx#3347).
 */
function focusPopoverEditor() {
  popoverEditorRef.value?.focus?.();
}

// The popover panel is lazy (unmounts on close) — this pre-flush watcher runs
// before that unmount, so closing by any path (outside click, Escape) commits.
// `ignoreClose` stops the watcher from re-entering after a successful commit.
let ignorePopoverClose = false;
watch(popoverOpen, (open) => {
  if (open) return;
  if (ignorePopoverClose) {
    ignorePopoverClose = false;
    return;
  }
  popoverOpen.value = true;
  void commitPopoverEditor().then((saved) => {
    if (!saved) return;
    ignorePopoverClose = true;
    popoverOpen.value = false;
  });
});

/** Carry a popover draft into the full modal (footer Open + notepad). */
async function expandFromPopover() {
  if (popoverOpen.value) {
    if (!(await commitPopoverEditor())) return;
    ignorePopoverClose = true;
    popoverOpen.value = false;
  }
  openEditor();
}

/**
 * Trigger-level guard: a disabled field must not toggle the popover. The
 * second click of a double click (BLP/nx#2818) hands off to the full editor:
 * the first one already opened the quick editor, so its draft goes along, and
 * NxPopover must not toggle it shut on its own.
 */
function onPreviewCaptureClick(e: MouseEvent) {
  if (props.disabled) {
    e.stopPropagation();
    return;
  }
  if (e.detail >= 2) {
    e.stopPropagation();
    void expandFromPopover();
  }
}

/** Enter opens the quick editor, Shift+Enter the full one (BLP/nx#2818). */
function onPreviewEnter(e: KeyboardEvent) {
  if (props.disabled) return;
  if (e.shiftKey) void expandFromPopover();
  else popoverOpen.value = true;
}
// ---------------------------------------------------------------------------

watch(
  () => {
    return { editor: smallEditorRef.value, value: modelValue.value };
  },
  ({ editor, value }, previous) => {
    if (!editor) return;
    const editorJustMounted = editor !== previous?.editor;
    // Seed on mount and when idle. While this surface is focused, inbound
    // modelValue (host PATCH echo after Ctrl/S) must not wipe later typing.
    if (inFocus.value && !isOpen.value && !editorJustMounted) return;
    // An idle echo of this editor's own HTML must not rebuild the document.
    // setHTML drops the caret that survived the picker blur, so the next
    // insertHTML lands at the start.
    if (editorJustMounted || editor.getHTML() !== value) editor.setHTML(value);
  }
);
let fullEditorSaved = false;
/** True from restoreDraft start until the recovered HTML is applied last. */
let restoreInFlight = false;
watch(
  () => {
    return { editor: editorRef.value, value: modelValue.value };
  },
  ({ editor, value }, previous) => {
    if (!editor || restoreInFlight) return;
    // Seed when the modal editor first appears (or after our own Save). While
    // it is open, inbound modelValue (host PATCH echo) must not wipe typing.
    const editorJustMounted = editor !== previous?.editor;
    if (isOpen.value && !editorJustMounted && !fullEditorSaved) return;
    editor.setHTML(value);
  }
);

async function onInlineFocusout(): Promise<void> {
  inFocus.value = false;
  focusRestoredByClose = false;
  await commitInlineEditor();
}

async function commitInlineEditor(): Promise<boolean> {
  const editor = smallEditorRef.value;
  if (!editor || props.disabled) return false;
  try {
    const pending = editor.prepareSave?.();
    if (!pending) {
      modelValue.value = editor.getHTML();
      return true;
    }
    const html = editor.getHTML();
    await pending;
    // prepareSave already succeeded — write the drained instance even if the
    // Vue ref was replaced (host remount on blur). Pre-await HTML is only a
    // fallback if that instance's getHTML throws.
    commitEditorHtml(editor, html);
    return true;
  } catch {
    /* The surface reports a failed pending import and retains the draft. */
    return false;
  }
}

/** Carry an inline draft into the full modal (notepad).
 *  Icon mousedown is preventDefault'd, so @focusout never commits. */
async function expandFromInline() {
  if (!(await commitInlineEditor())) return;
  openEditor();
}

// --- inline double click (BLP/nx#2818) --------------------------------------
/**
 * A double click whose first press found the field inactive opens the full
 * editor; inside an active field a double click keeps selecting a word. The
 * field remembers whether each of its last two presses found it inactive: when
 * the dblclick arrives, the earlier one is the double click's first press.
 * The press is a pointerdown: Write's canvas cancels it, so no mousedown (and
 * no click count) reaches the field — but the dblclick still does.
 */
let earlierPressFoundInactive = false;
let lastPressFoundInactive = false;
/**
 * The focus is in the inline editor only because the full editor's close put
 * it back (`restoreFieldFocus`), not because the user activated the field: the
 * next press still finds the field inactive. It ends with the user's first
 * press, key, edit or the focus leaving the field.
 */
let focusRestoredByClose = false;

/** `field` is the field's frame: the field is active while the focus is inside it. */
function noteInlinePress(field: EventTarget | null): void {
  earlierPressFoundInactive = lastPressFoundInactive;
  lastPressFoundInactive =
    focusRestoredByClose || !(field instanceof Node && field.contains(document.activeElement));
  focusRestoredByClose = false;
}

function onInlineDraftChange(html: string): void {
  focusRestoredByClose = false;
  if (!isOpen.value) emit('draft-change', html);
}

function openFromDoubleClick(event: Event): void {
  if (props.disabled || isOpen.value || !earlierPressFoundInactive) return;
  earlierPressFoundInactive = false;
  // Keep the second press from selecting a word under the opening modal.
  event.preventDefault();
  void expandFromInline();
}

// --- press on the empty area of the inline field (BLP/nx#3318) --------------
/**
 * The inline editor is only as tall as its text (a frame editor sizes itself to
 * the content), the field around it up to `maxLines` or more: a press below the
 * text lands on the frame or the surface's own root, not in the editor. The
 * strip at the frame's edge is the browser's: an overlay scrollbar or the
 * native resize handle.
 */
const FRAME_EDGE_PX = 16;

/**
 * A plain primary press on the frame (`event.currentTarget`) or the editor surface's
 * root, beside the text. Not on a disabled field, not inside the editor itself
 * (a surface without a frame lets its events bubble — those are the editor's),
 * not on the frame's scrollbars, border or resize handle.
 */
function isEmptyAreaPress(event: MouseEvent): boolean {
  const frame = event.currentTarget;
  if (props.disabled || event.button !== 0 || !(frame instanceof HTMLElement)) return false;
  // Shift extends a selection, Ctrl-click is the context menu on a Mac.
  if (event.shiftKey || event.ctrlKey || event.metaKey || event.altKey) return false;
  if (event.target !== frame && event.target !== smallEditorRef.value?.$el) return false;
  const rect = frame.getBoundingClientRect();
  const x = event.clientX - rect.left - frame.clientLeft;
  const y = event.clientY - rect.top - frame.clientTop;
  if (x < 0 || y < 0 || x >= frame.clientWidth || y >= frame.clientHeight) return false;
  const nearRight = x >= frame.clientWidth - FRAME_EDGE_PX;
  const nearBottom = y >= frame.clientHeight - FRAME_EDGE_PX;
  if (nearRight && frame.scrollHeight > frame.clientHeight) return false;
  if (nearBottom && frame.scrollWidth > frame.clientWidth) return false;
  return !(nearRight && nearBottom && props.resizable && !props.fill);
}

function onInlinePointerDown(event: PointerEvent): void {
  if (event.button === 0) noteInlinePress(event.currentTarget);
}

/**
 * Whether the inline editor can take the focus: a frame editor reports the
 * frame itself as its `inputElement` until it has loaded, its body afterwards.
 * A press before that is left alone — cancelling it would swallow it.
 */
function inlineEditorReady(): boolean {
  const surface = smallEditorRef.value?.inputElement;
  return !!surface && !(surface instanceof HTMLIFrameElement);
}

/**
 * Focuses the editor on the mousedown, not the pointerdown: a cancelled
 * pointerdown suppresses the mousedown in Chromium, and the page's outside-press
 * handlers (pickers, menus) listen for it; cancelling the mousedown keeps it
 * dispatched and is what stops the frame (tabindex -1) taking the focus in every
 * engine. A touch or pen swipe starting here sends no mousedown, only a tap does,
 * so a swipe does not focus the field. The double click logic reads pointerdown
 * and dblclick, unaffected.
 */
function onInlineMouseDown(event: MouseEvent): void {
  if (!isEmptyAreaPress(event) || !inlineEditorReady()) return;
  event.preventDefault();
  // `atEnd`: an older selection somewhere in the text must not win over the press.
  void smallEditorRef.value?.focus?.({ atEnd: true });
}
// ---------------------------------------------------------------------------

/**
 * An editor in a frame (NxHtmlEditor) reports its presses as `nx-editor-press`
 * with their click count: no pointer event leaves the frame.
 */
function onInlineFramePress(event: Event): void {
  const clickCount = (event as CustomEvent<{ clickCount?: number }>).detail?.clickCount ?? 1;
  noteInlinePress(event.currentTarget);
  if (clickCount >= 2) openFromDoubleClick(event);
}

function cancelFullEditor() {
  isOpen.value = false;
}
async function saveFullEditor() {
  const editor = editorRef.value;
  if (props.disabled || !editor) return;
  try {
    const pending = editor.prepareSave?.();
    if (pending) await pending;
  } catch {
    /* Keep the last good model — do not emit pre-decode or empty HTML. */
    return;
  }
  // prepareSave already succeeded — write the drained instance even if the
  // host disabled, closed, or remounted mid-await (same as popover/inline).
  // getHTML throw (size/island) is not a save — keep last good model and the modal.
  if (!commitEditorHtml(editor)) return;
  fullEditorSaved = true;
  if (isOpen.value) isOpen.value = false;
}

function openEditor() {
  if (!props.disabled) {
    isOpen.value = true;
  }
}

watch(isOpen, (open, previous) => {
  if (open) fullEditorSaved = false;
  else if (previous && !fullEditorSaved) emit('draft-discard');
  if (open) {
    // NxModal keeps the body mounted through the close transition; always
    // reseed so a reused instance does not show a discarded draft — except
    // while restoreDraft is applying recovered HTML (that must win).
    if (!restoreInFlight) {
      editorRef.value?.setHTML(modelValue.value ?? '');
    }
    // showModal() moves focus to the dialog chrome (close/save); reclaim it.
    void focusEditor(() => editorRef.value);
  }
});

function restoreFieldFocus() {
  // Native dialogs keep the rest of the page inert until their exit ends.
  if (isOpen.value || props.disabled) return;
  if (props.popoverEdit) {
    previewRef.value?.focus();
    return;
  }
  smallEditorRef.value?.focus();
  focusRestoredByClose = true;
}

function liveEditor(): EditorSurface | null {
  if (isOpen.value) return editorRef.value;
  if (props.popoverEdit) return popoverEditorRef.value;
  return smallEditorRef.value;
}

function commitEditorHtml(editor: EditorSurface, fallbackHtml?: string): boolean {
  try {
    // Always read the instance that drained prepareSave — a replaced Vue ref
    // must not fall back to a pre-decode snapshot (that drops a finished <img>).
    modelValue.value = editor.getHTML();
    return true;
  } catch {
    if (fallbackHtml !== undefined) {
      modelValue.value = fallbackHtml;
      return true;
    }
    /* Keep the last good model — do not emit "". */
    return false;
  }
}

/**
 * Await pending media, then publish HTML. Never snapshot before prepareSave —
 * a pre-decode getHTML drops in-flight images. Hosts should await this before
 * unmounting for in-flight media; onBeforeUnmount cannot delay Vue destroying
 * the child editor, but still must not lose typed text (sync-commit below).
 * A second call after a completed save still getHTMLs the post-decode document.
 */
async function flush(): Promise<void> {
  // Host teardown of an open modal is Cancel, not Save — do not write
  // recovered/unsaved modal HTML into modelValue (that would auto-publish).
  if (isOpen.value) return;
  const editor = liveEditor();
  if (!editor || props.disabled) return;
  try {
    const pending = editor.prepareSave?.();
    if (pending) await pending;
  } catch {
    /* Keep the last good model — do not emit pre-decode or empty HTML. */
    return;
  }
  commitEditorHtml(editor);
}

onBeforeUnmount(() => {
  // Host teardown of an open modal is Cancel — do not write.
  if (isOpen.value) return;
  const editor = liveEditor();
  if (!editor || props.disabled) return;
  // Sync-commit while still mounted. Write's prepareSave is always a Promise,
  // so flush() would only emit after await — Vue then drops emit (isUnmounted).
  commitEditorHtml(editor);
  const pending = editor.prepareSave?.();
  if (pending) {
    void pending.then(
      () => commitEditorHtml(editor),
      () => {
        /* Keep the sync snapshot — do not emit "". */
      }
    );
  }
});

const i18nInjection = inject(i18nInjectionKey)!;

async function restoreDraft(html: string): Promise<void> {
  if (props.disabled) return;
  restoreInFlight = true;
  try {
    openEditor();
    for (let i = 0; i < 20 && !editorRef.value; i++) await nextTick();
    if (!editorRef.value) throw new Error('Editor is not ready');
    // Flush queued open-reseed / mount watches (they skip while in flight).
    await nextTick();
    editorRef.value.setHTML(html);
    editorRef.value.getHTML();
    editorRef.value.focus();
    await nextTick();
  } finally {
    restoreInFlight = false;
  }
}

defineExpose({
  restoreDraft,
  flush,
  prepareUnmount: flush,
  inputElement: computed(() =>
    props.popoverEdit ? (previewRef.value ?? null) : (smallEditorRef.value?.inputElement ?? null)
  ),
  editor: computed(() => {
    if (isOpen.value) return editorRef.value;
    if (props.popoverEdit) return popoverEditorRef.value;
    return smallEditorRef.value;
  }),
  /** Whether the full-size modal editor is open. */
  isModalOpen: computed(() => isOpen.value),
  /**
   * popoverEdit: open the quick editor as a click on the preview would — for a
   * host that knows the user meant this field (a grid cell activated by a
   * click on its description line). Ignored when disabled or not popoverEdit,
   * and while the full editor is open: a host's call deferred from the first
   * press of a double click arrives after the modal opened, and the quick
   * editor would then sit over it and take its wheel (BLP/nx#2861).
   */
  openPopover: () => {
    if (props.popoverEdit && !props.disabled && !isOpen.value) popoverOpen.value = true;
  },
});
defineOptions({ inheritAttrs: false });
</script>

<template>
  <!-- popoverEdit: truncated one-line preview; click opens the popover editor. -->
  <div v-if="props.popoverEdit" class="nx-editor-popover-host" :style="props.wrapperStyle">
    <NxPopover
      v-model:open="popoverOpen"
      side="bottom-start"
      lazy
      escape-from-fields
      @opened="focusPopoverEditor"
    >
      <template #trigger>
        <NxFakeInput
          v-bind="$attrs"
          :id="props.id"
          :wrapper-style="{ width: '100%' }"
          :style="{ overflow: 'hidden', cursor: props.disabled ? undefined : 'pointer' }"
          :icons="[
            {
              align: 'end',
              component: NotepadIcon,
              filled: true,
              color: 'var(--nxt-brand-bg)',
              label: i18nInjection.t('common.editor.open'),
              tooltip: i18nInjection.t('common.editor.open_tooltip_quick'),
              onClick: async (e: PointerEvent) => {
                // Keep the click off the popover anchor so NxPopover does not
                // toggle; flush any popover draft, then hand off to the modal.
                e.stopPropagation();
                await expandFromPopover();
              },
              revealOnInteraction: props.revealIcon,
            },
          ]"
          :height="props.height"
          :data-disabled="props.disabled ? true : undefined"
          :data-focus-visible="popoverOpen || isOpen || inFocus ? true : undefined"
          tabindex="-1"
          @click.capture="onPreviewCaptureClick"
        >
          <span
            ref="preview-ref"
            class="editor-preview"
            :tabindex="props.disabled ? undefined : 0"
            @focusin="inFocus = true"
            @focusout="inFocus = false"
            @keydown.enter.prevent="onPreviewEnter"
          >
            {{ previewLine }}
          </span>
        </NxFakeInput>
      </template>
      <div class="editor-popover-body">
        <component
          :is="props.editorComponent ?? NxEditor"
          v-bind="props.editorProps"
          :disabled="props.disabled"
          :label="props.editorTitle"
          ref="popover-editor-ref"
          class="editor-popover-editor"
          :show-action-bar="false"
          :internal-scroll="true"
          :focus-on-load="true"
          @update:model-value="emit('draft-change', $event)"
          @focusout="commitPopoverEditor"
          @save="commitPopoverEditor"
        />
        <div class="editor-popover-actions">
          <NxButton variant="outline" height="s" @click="expandFromPopover">
            {{ i18nInjection.t('common.editor.open') }}
          </NxButton>
        </div>
      </div>
    </NxPopover>
  </div>

  <NxFakeInput
    v-else
    v-bind="$attrs"
    :id="props.id"
    :wrapper-style="wrapperStyle"
    :style="fakeInputStyle"
    :icons="[
      {
        align: 'end',
        component: NotepadIcon,
        filled: true,
        color: 'var(--nxt-brand-bg)',
        label: i18nInjection.t('common.editor.open'),
        tooltip: i18nInjection.t('common.editor.open_tooltip'),
        onClick: expandFromInline,
        revealOnInteraction: props.revealIcon,
      },
    ]"
    :height="props.height"
    :multiline="props.resizable"
    :data-disabled="props.disabled ? true : undefined"
    :data-focus-visible="isOpen || inFocus ? true : undefined"
    tabindex="-1"
    @pointerdown.capture="onInlinePointerDown"
    @mousedown.capture="onInlineMouseDown"
    @keydown.capture="focusRestoredByClose = false"
    @input.capture="focusRestoredByClose = false"
    @dblclick.capture="openFromDoubleClick"
    @nx-editor-press="onInlineFramePress"
  >
    <!-- tabindex -1 as the editor has a tab-index -->

    <component
      :is="props.editorComponent ?? NxEditor"
      v-bind="props.editorProps"
      :disabled="props.disabled"
      :label="props.editorTitle"
      ref="small-editor-ref"
      class="nx-editor-input__inline-editor"
      :style="props.fill ? { flex: '1 0 auto' } : undefined"
      :show-action-bar="false"
      :focus-on-load="false"
      :internal-scroll="false"
      @update:model-value="onInlineDraftChange"
      @focusin="inFocus = true"
      @focusout="onInlineFocusout"
      @save="commitInlineEditor"
    />
  </NxFakeInput>

  <!-- `fill` here is NxModal's: the dialog's body never scrolls, the editor does. -->
  <NxModal
    v-model:open="isOpen"
    class="nx-editor-input__modal"
    fill
    :close-on-click-outside="false"
    :storage-key="props.modalStorageKey"
    @modal-closed="restoreFieldFocus"
  >
    <template #header>{{ editorTitle }}</template>
    <template #body>
      <!-- real editor to edit -->
      <component
        :is="props.editorComponent ?? NxEditor"
        v-bind="props.editorProps"
        :disabled="props.disabled"
        :label="props.editorTitle"
        ref="editor-ref"
        class="nx-editor-input__modal-editor"
        :focus-on-load="true"
        :insert-tab-character="true"
        @update:model-value="emit('draft-change', $event)"
        @save="saveFullEditor"
      />
    </template>
    <template #footer>
      <NxButton :disabled="props.disabled" @click="saveFullEditor">
        {{ i18nInjection.t('common.editor.save') }}
      </NxButton>
      <NxButton variant="outline" @click="cancelFullEditor">
        {{ i18nInjection.t('common.editor.cancel') }}
      </NxButton>
    </template>
  </NxModal>
</template>

<style scoped>
/* The editor fills the window: its natural size grows with a sized window, and a
   small window may still shrink the height. The modal's body (`fill`) never scrolls;
   only the editor does. Scoped under the modal on purpose: an editor component's own
   root rule (the HTML editor's `min-height`, the Write editor's width/height: 100%) has
   the same specificity and loads later, which kept the editor from shrinking or sizing. */
.nx-editor-input__modal .nx-editor-input__modal-editor {
  flex: 1 1 auto;
  min-width: 70vw;
  height: 70vh;
  min-height: 0;
}

.nx-editor-popover-host {
  display: flex;
  min-width: 0;
}

/* The quick editor is wider than a popover's default cap; let the panel fit
   it (the editor's own width below still stops at 80vw). */
.nx-editor-popover-host :deep(.nx-popover-panel) {
  max-width: 90vw;
}

/* The popover anchor must take over the field's flex sizing inside the host. */
.nx-editor-popover-host :deep(.nx-popover-anchor) {
  display: block;
  flex: 1 1 auto;
  min-width: 0;
}

.editor-preview {
  display: block;
  flex: 1 1 auto;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  outline: none;
}

/* The inline editor sits inside the filled field: its own page surface
   (a white sheet in the Write editor) must not paint a box inside the field. */
.nx-editor-input__inline-editor {
  --nxt-surface-bg: transparent;

  background: transparent;
}

.editor-popover-body {
  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-2xs);
}

/* Fixed width and a hard height cap: a short text keeps the editor small, a
   long one scrolls inside it (internal-scroll) and "Open" stays in reach.
   Scoped under the body on purpose: an editor component's own root rule (the
   Write editor's width/height: 100%) has the same specificity and loads later,
   which let the quick editor grow with its text past the screen. */
.editor-popover-body > .editor-popover-editor {
  width: min(var(--nxt-panel-width-4xl), 80vw);
  height: auto;
  max-height: min(var(--nxt-area-size-2xl), 60vh);
}

.editor-popover-actions {
  display: flex;
  justify-content: flex-end;
}
</style>

<style>
/* Unscoped, keyed on our own class: the inline editor's paragraphs are editor
   content, out of reach of scoped rules. They take the field's control line
   instead of the global reading line (font.css `p`/`ul`), so a one-line editor
   field is exactly as tall as every other field of its `height`, and `maxLines`
   counts real lines. An explicit line spacing set in the text still wins; a
   plain table cell keeps its own line (style.css), so its row stays as it was. */
.input-wrapper:not([data-plain-cell]) .nx-editor-input__inline-editor :is(p, ul, ol) {
  line-height: inherit;
}
</style>
